import React, { useState } from 'react';
import {
    StyleSheet,
    View,
    Image,
    TouchableOpacity,
    Platform,
} from 'react-native';
import { ODSText } from '../ODSText';
import getScreenType from '../../../utils/getScreenType';
import { getTokens } from '../../../design-tokens/color-tokens';
import { ODSIcon } from '../ODSIcon';
import ODSBadges from '../ODSBadges';
import ODSButton from '../ODSButton';
import GradientWrapper from '../../molecules/openflowHome/GradientWrapper';

const ODSBrick = React.memo(({
    nonChild,
    title,
    subtitle,
    image,
    onPress,
    children,
    status,
    goButton,
    safehouse,
    gradientColors,
    accentColor,
}: {
    nonChild?: boolean;
    title?: string;
    subtitle?: string;
    image?: any;
    onPress?: () => void;
    children?: React.ReactNode;
    status?: string;
    goButton?: string;
    safehouse?: boolean;
    gradientColors?: string[];
    accentColor?: string;
}) => {
    const colors = React.useMemo(() => getTokens('light').colors, []);
    const layout = getScreenType();
    const hasChildren = React.Children.count(children) > 0;
    const [expanded, setExpanded] = useState(true);
    const [isHovered, setIsHovered] = useState(false);

    const handlePress = React.useCallback(() => {
        if (hasChildren && !nonChild) {
            setExpanded((prev) => !prev);
        } else {
            onPress?.();
        }
    }, [hasChildren, onPress]);

    const hoverProps = Platform.OS === 'web' ? {
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
    } : {};

    const accent = accentColor || colors.button.main;

    const defaultGradient = safehouse
        ? [accent, accent]
        : gradientColors || [colors.main.blue[200], colors.main.blue[100]];

    // ── Web / Tablet: horizontal card with left accent ──
    if (layout !== 'phone') {
        return (
            <View
                style={[
                    webStyles.card,
                    safehouse && { backgroundColor: '#E3F8FA' },
                    isHovered && webStyles.cardHover,
                    { borderLeftColor: accent },
                ]}
                {...hoverProps}
            >
                <TouchableOpacity
                    onPress={!goButton ? handlePress : null}
                    activeOpacity={0.7}
                    style={webStyles.cardInner}
                >
                    {/* Icon */}
                    <GradientWrapper
                        colors={defaultGradient}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={webStyles.iconWrap}
                    >
                        {image ? (
                            <Image source={image} style={{ width: 28, height: 28, resizeMode: 'contain' }} />
                        ) : (
                            <Image source={require('../../../images/icons/dashboard/safehouse.png')} style={{ width: 36, height: 36, resizeMode: 'contain' }} />
                        )}
                    </GradientWrapper>

                    {/* Text */}
                    <View style={webStyles.textBlock}>
                        <ODSText type='md-bold' color={safehouse ? '#0C4A6E' : colors.text.primary} marginTop={0}>{title}</ODSText>
                        <ODSText type='sm' color={safehouse ? '#0C4A6E99' : colors.text.grey} marginTop={4} numberOfLines={3}>{subtitle}</ODSText>
                        {(status && status !== "Not Started") ? (
                            <ODSBadges restStyle={{ marginTop: 10, alignSelf: 'flex-start' }} type={status}>
                                {status}
                            </ODSBadges>
                        ) : null}
                        {goButton && <ODSButton
                            restStyle={{ height: 40, width: 100, marginTop: 12 }}
                            type="primary"
                            disabled={false}
                            themeColor={goButton}
                            onPress={onPress}>
                            {"Go"}
                        </ODSButton>}
                    </View>

                    {/* Arrow */}
                    {(!goButton) ? (
                        <View style={[webStyles.arrowWrap, { backgroundColor: accent + '18' }]}>
                            <ODSIcon
                                icon={(hasChildren && !nonChild) ? (expanded ? 'TB-Angle-Up' : 'TB-Angle-Down') : 'TB-Arrow-Right'}
                                color={accent}
                                size={18}
                            />
                        </View>
                    ) : null}
                </TouchableOpacity>

                {(hasChildren) && expanded ? (
                    <View style={{ marginTop: 12, paddingLeft: 68 }}>{children}</View>
                ) : null}
            </View>
        );
    }

    // ── Mobile: compact horizontal row ──
    const mobileStyles = styles(colors, layout);
    return (
        <View
            style={[
                mobileStyles.containerStyle,
                safehouse && mobileStyles.safehouseStyle,
                { borderLeftWidth: 4, borderLeftColor: accent },
            ]}
            {...hoverProps}
        >
            <TouchableOpacity
                onPress={!goButton ? handlePress : null}
                activeOpacity={0.7}
                style={mobileStyles.parentContainer}
            >
                <GradientWrapper
                    colors={defaultGradient}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={mobileStyles.iconContainer}
                >
                    {image ? (
                        <Image source={image} style={{ width: 24, height: 24, resizeMode: 'contain' }} />
                    ) : (
                        <Image source={require('../../../images/icons/dashboard/safehouse.png')} style={{ width: 32, height: 32, resizeMode: 'contain' }} />
                    )}
                </GradientWrapper>

                <View style={{ flex: 1, paddingHorizontal: 12 }}>
                    <ODSText type='sm-bold' color={safehouse ? '#19357D' : colors.text.black} marginTop={0}>{title}</ODSText>
                    <ODSText type='sm' color={safehouse ? 'rgba(25, 53, 125, 0.75)' : colors.text.grey} marginTop={4}>{subtitle}</ODSText>
                    {(status && status != "Not Started") ? (
                        <ODSBadges restStyle={{ marginTop: 8, alignSelf: 'flex-start' }} type={status}>
                            {status}
                        </ODSBadges>
                    ) : null}
                    {goButton && <ODSButton
                        restStyle={{ height: 40, width: 100, marginTop: 12 }}
                        type="primary"
                        disabled={false}
                        themeColor={goButton}
                        onPress={onPress}>
                        {"Go"}
                    </ODSButton>}
                </View>

                {(!goButton) ? (
                    <View style={[mobileStyles.arrowContainer, safehouse && { backgroundColor: 'rgba(25, 53, 125, 0.1)' }]}>
                        <ODSIcon
                            icon={(hasChildren && !nonChild) ? (expanded ? 'TB-Angle-Up' : 'TB-Angle-Down') : 'TB-Arrow-Right'}
                            color={safehouse ? '#19357D' : colors.button.main}
                            size={18}
                        />
                    </View>
                ) : null}
            </TouchableOpacity>

            {(hasChildren) && expanded ? (
                <View style={{ marginTop: 12 }}>{children}</View>
            ) : null}
        </View>
    );
});

// ── Web styles (not using StyleSheet because we need CSS transitions) ──
const webStyles = StyleSheet.create({
    card: {
        display: 'flex',
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        borderLeftWidth: 4,
        borderLeftColor: '#1FA6B2',
        padding: 20,
        paddingVertical: 22,
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 8,
        elevation: 2,
        ...(Platform.OS === 'web' ? {
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
        } : {}),
    },
    cardHover: {
        shadowOpacity: 0.1,
        shadowRadius: 20,
        shadowOffset: { width: 0, height: 6 },
        ...(Platform.OS === 'web' ? {
            transform: [{ translateY: -2 }],
        } : {}),
    },
    cardInner: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
    },
    iconWrap: {
        width: 52,
        height: 52,
        borderRadius: 14,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 16,
        flexShrink: 0,
    },
    textBlock: {
        flex: 1,
        paddingRight: 12,
    },
    arrowWrap: {
        width: 38,
        height: 38,
        borderRadius: 19,
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
    },
});

// ── Mobile styles ──
const styles = (theme: any, layout: string) =>
    StyleSheet.create({
        containerStyle: {
            width: '100%',
            borderRadius: 14,
            backgroundColor: '#FFFFFF',
            padding: 16,
            shadowColor: '#0F172A',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.04,
            shadowRadius: 6,
            elevation: 2,
        },
        safehouseStyle: {
            backgroundColor: '#E3F8FA',
        },
        parentContainer: {
            flexDirection: 'row',
            alignItems: 'center',
        },
        iconContainer: {
            width: 48,
            height: 48,
            borderRadius: 14,
            justifyContent: 'center',
            alignItems: 'center',
        },
        arrowContainer: {
            width: 36,
            height: 36,
            borderRadius: 18,
            backgroundColor: theme.main.blue[100],
            justifyContent: 'center',
            alignItems: 'center',
        },
    });

export default ODSBrick;
